import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getToken } from '../../Authentication/get-token';
import { SOLAR_ENERGY_BASE_API_URL } from '../../../strings';
import { mockApiCall } from './mock-data';
import { formatLastUpdated } from './forecast-utils';

/**
 * Custom hook for managing probabilistic forecast data
 */
export const useForecastData = (config, isActive = true) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  
  const abortControllerRef = useRef(null);
  const timeoutRef = useRef(null);

  /**
   * Fetch data from API or mock service
   */
  const fetchData = useCallback(async (isRetry = false) => {
    if (!isActive) return;

    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    
    if (!isRetry) {
      setIsLoading(true);
    }
    setError(null);

    try {
      let result;
      
      if (config.mockData?.enabled || config.widget?.enableMockData) {
        // Use mock data
        result = await mockApiCall(config, 500);
      } else {
        // Use real API
        const tokenObj = getToken();
        const headers = {
          'Content-Type': 'application/json',
          ...(tokenObj?.headers || {}),
        };

        const url = `${SOLAR_ENERGY_BASE_API_URL}${config.api.endpoint}`;
        const response = await axios.get(url, {
          headers,
          timeout: config.api.timeout || 30000,
          signal: abortControllerRef.current.signal
        });

        result = response.data;
      }

      // Validate response structure
      if (!result || !Array.isArray(result.data)) {
        throw new Error(config.errorMessages?.invalidData || 'Invalid data format');
      }

      setData(result.data);
      setLastUpdated(formatLastUpdated());
      setRetryCount(0); // Reset retry count on success
      setError(null);

    } catch (err) {
      if (err.name === 'AbortError') {
        return; // Request was cancelled, don't update state
      }

      console.error('Error fetching forecast data:', err);
      
      const shouldRetry = retryCount < (config.api?.retryAttempts || 3);
      
      if (shouldRetry && !isRetry) {
        // Exponential backoff for retries
        const delay = (config.api?.retryDelay || 2000) * Math.pow(2, retryCount);
        
        timeoutRef.current = setTimeout(() => {
          setRetryCount(prev => prev + 1);
          fetchData(true);
        }, delay);
        
        return;
      }

      setError({
        message: config.errorMessages?.fetchError || 'Failed to fetch data',
        canRetry: true,
        originalError: err
      });
      setData([]);
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  }, [isActive, config, retryCount]);

  /**
   * Manual retry function
   */
  const retry = useCallback(() => {
    setRetryCount(0);
    setError(null);
    fetchData();
  }, [fetchData]);

  /**
   * Setup periodic refresh
   */
  useEffect(() => {
    if (!isActive) return;

    fetchData();

    const intervalId = setInterval(() => {
      fetchData();
    }, config.widget?.refreshInterval || 900000); // Default 15 minutes

    return () => {
      clearInterval(intervalId);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData, isActive]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    data,
    isLoading,
    error,
    lastUpdated,
    retry,
    retryCount
  };
};
